<template>
  <div>
    <v-btn
      icon
      elevation="13"
      color="exercise darken-1"
      class="accent lighten-5"
      :disabled="!chat"
      @click="openQuickResolve(chat)"
    >
      <v-icon>mdi-chat</v-icon>
    </v-btn>

    <v-dialog v-model="quickResolve.dialog" width="500" persistent scrollable>
      <v-card :class="{ 'h-500': quickResolve.type === 'text' }">
        <div class="d-flex justify-space-between">
          <v-card-title>Quick Resolve</v-card-title>
          <v-btn class="mt-2 mr-2" icon @click="close()">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text>
          <v-btn-toggle v-model="quickResolve.type" tile color="secondary accent-3" group class="mb-4">
            <v-btn value="react">
              React
            </v-btn>
            <v-btn value="text">
              Message
            </v-btn>
            <v-btn value="voicenote">
              Voicenote
            </v-btn>
            <v-btn value="video">
              Video
            </v-btn>
          </v-btn-toggle>

          <!-- message-with-emoji -->
          <j-emoji-textarea
            v-if="quickResolve.type === 'text'"
            v-model="quickResolve.text"
            auto-grow
            dense
            rows="1"
            placeholder="Message"
            ref="textRef"
            class="flex-grow-1"
          />

          <!-- message -->
          <v-select
            v-model="quickResolve.emoji"
            v-else-if="quickResolve.type === 'react'"
            :items="emojis"
            label="Select Reaction"
            persistent-hint
            hint="This will react to the latest message from the CLIENT"
          />

          <!-- voicenote -->
          <div v-else-if="quickResolve.type === 'voicenote'" class="d-flex align-center">
            <audio-recorder @send-audio="saveRemoteFile($event, 'AUDIO')" />
            <div v-if="remoteFile && attachmentType === 'AUDIO'" class="d-flex align-center">
              <v-icon color="green" class="ml-4">mdi-check-circle</v-icon>
              <div class="caption ml-2">Click "Send" button to send</div>
            </div>
          </div>

          <!-- video -->
          <div v-else-if="quickResolve.type === 'video'" class="d-flex align-center">
            <video-recorder @send-video="saveRemoteFile($event, 'VIDEO')" />
            <div v-if="remoteFile && attachmentType === 'VIDEO'" class="d-flex align-center">
              <v-icon color="green" class="ml-4">mdi-check-circle</v-icon>
              <div class="caption ml-2">Click "Send" button to send</div>
            </div>
          </div>
        </v-card-text>

        <div v-if="loading" class="mt-2 px-4">
          <upload-progress :upload-percent-completed="uploadPercentCompleted" />
        </div>

        <v-card-actions>
          <v-checkbox v-if="quickResolve.type !== 'react'" v-model="sendEmailNotification" label="Send email" />
          <v-spacer />
          <v-btn color="secondary" @click="quickResolveChat" :disabled="!buttonEnabled">Send</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { QUICK_RESOLVE_CHAT_MUTATION } from "@/graphql/mutations/chat";
import { decodeId } from "@/lib/string";
import VideoRecorder from "@/components/chats/VideoRecorder";
import AudioRecorder from "@/components/chats/AudioRecorder";
import CloudinaryMixin from "@/components/shared/mixins/CloudinaryMixin";
import UploadProgress from "@/components/shared/UploadProgress";
import JEmojiTextarea from "@/components/shared/inputs/JEmojiTextarea";

export default {
  name: "ResolveMessage",
  mixins: [CloudinaryMixin],
  components: {
    VideoRecorder,
    UploadProgress,
    AudioRecorder,
    JEmojiTextarea,
  },
  props: {
    resolved: {
      type: Boolean,
      default: false,
    },
    chat: {
      type: Object,
      required: false,
    },
  },

  data() {
    return {
      text: null,
      loading: false,
      quickResolve: {
        dialog: false,
        chat: null,
        type: "react",
        text: "",
        emoji: "👍",
      },
      emojis: ["👍", "👎", "❤️", "😭", "😕", "😐", "😊", "😍"],
      remoteFile: null,
      attachmentType: null,
      sendEmailNotification: false,
    };
  },
  computed: {
    buttonEnabled() {
      if (this.loading) {
        return false;
      }
      if (this.quickResolve.type === "text" && this.quickResolve.text.trim().length === 0) {
        return false;
      }
      if ((this.quickResolve.type === "voicenote" || this.quickResolve.type === "video") && !this.remoteFile) {
        return false;
      }
      return true;
    },
  },
  methods: {
    close() {
      this.quickResolve = {
        dialog: false,
        chat: null,
        type: "react",
        text: "",
        emoji: "👍",
      };
      this.uploadPercentCompleted = 0;
      this.remoteFile = null;
      this.attachmentType = null;
      this.sendEmailNotification = false;
      this.loading = false;
    },
    async saveRemoteFile(data, type) {
      this.loading = true;
      const result = await this.uploadFileToCloudinary(data.file);
      this.remoteFile = result.public_id;
      this.attachmentType = type;
      this.loading = false;
    },
    openQuickResolve(chat) {
      this.quickResolve.type = "react";
      this.quickResolve.chat = chat;
      this.quickResolve.text = "";
      this.quickResolve.dialog = true;
    },
    async quickResolveChat() {
      this.quickResolve.dialog = false;
      const text = this.quickResolve.type === "react" ? this.quickResolve.emoji : this.quickResolve.text;
      try {
        await this.$apollo.mutate({
          mutation: QUICK_RESOLVE_CHAT_MUTATION,
          variables: {
            chatId: this.quickResolve.chat.id,
            text: text,
            type: this.quickResolve.type,
            userId: decodeId(this.$store.getters.user.id),
            remoteFile: this.remoteFile,
            attachmentType: this.attachmentType,
            sendEmailNotification: this.sendEmailNotification,
          },
          refetchQueries: ["coachTaskNumbers", "coachingSubscriptionNumbers", "coachDashboardListing"],
        });

        this.$toasted.success("Message sent!", {
          duration: 2000,
          position: "bottom-center",
        });
      } catch (e) {
        this.$toasted.error("Unable to send message at this time, please try again", {
          duration: 2000,
          position: "bottom-center",
        });
      }
      this.close();
    },
  },
};
</script>
<style scoped>
.h-500 {
  height: 500px;
}
</style>
